import {
    useContext, useEffect, useState
} from 'react';
import {
    AlertCircle, CheckCircle2, Cloud, Pause
} from 'lucide-react';
import {
    Card, CardContent, CardFooter, CardHeader, CardTitle
} from 'shared/components/ui/card';
import {
    Button
} from 'shared/components/ui/button';
import {
    Badge
} from 'shared/components/ui/badge';
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from 'shared/components/ui/alert-dialog';
import {
    OrganizationContext
} from 'shared/context/OrganizationContext';
import {
    organizationService
} from 'shared/api';
import {
    Context
} from 'shared/context/GlobalState';
import {
    Input
} from 'shared/components/ui/input';

export default function OrganizationCluster() {

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isTerminating, setIsTerminating] = useState(false);
    const [inputClusterCheckName, setInputClusterCheckName] = useState('');

    const {
        addNewNotifcation
    } = useContext(Context);
    const {
        selectedOrganization, getUserRole
    } = useContext(OrganizationContext);
    const roleInstance = getUserRole();

    const handleTerminate = () => {
        if (!roleInstance.isSuperAdmin) {
            return addNewNotifcation('Unauthorized, Only super admin can perform this action', 'warning');
        }

        setIsTerminating(true);
        organizationService.deleteOrganizationCluster(selectedOrganization._id)
            .then(() => {

                addNewNotifcation('Cluster deleted successfully', 'success');
                setTimeout(() => {
                    window.location = '/organizations';
                }, 1000);
            }).catch((error) => {

                if (error.response.status === 403) {
                    addNewNotifcation('You don not have the right access to delete this cluster. Only super admin can perform this action', 'danger');
                } else if (error.response.status === 409) {
                    addNewNotifcation('Organization have active subscription, Please remove any active subscription before proceed', 'danger');
                } else {
                    addNewNotifcation('Something went wrong', 'danger');
                }
            }).finally(() => {
                setIsTerminating(false);
                setIsDialogOpen(false);
            });
    };

    useEffect(() => {
        setInputClusterCheckName('');
    }, [isDialogOpen]);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    return (
        <Card className="w-full p-6">
            <CardHeader className="p-0">
                <CardTitle className="flex items-center justify-between">
                    <span>{selectedOrganization.organizationCluster.name}</span>
                    <StatusIndicator stateName={selectedOrganization.organizationCluster.stateName} paused={selectedOrganization.organizationCluster.paused} />
                </CardTitle>
            </CardHeader>
            <CardContent className="mt-6 p-0">
                <dl className="divide-y divide-border">
                    <div className="flex items-center justify-between py-2.5">
                        <dt className="text-sm text-muted-foreground">Status</dt>
                        <Badge variant={selectedOrganization.organizationCluster.stateName === 'IDLE' ? 'default' : 'secondary'}>
                            {selectedOrganization.organizationCluster.stateName}
                        </Badge>
                    </div>
                    <div className="flex items-center justify-between py-2.5">
                        <dt className="text-sm text-muted-foreground">Created</dt>
                        <dd className="font-medium">{formatDate(selectedOrganization.organizationCluster.createDate)}</dd>
                    </div>
                    <div className="flex items-center justify-between py-2.5">
                        <dt className="text-sm text-muted-foreground">Disk Size</dt>
                        <dd className="font-medium">{selectedOrganization.organizationCluster.diskSizeGB} GB</dd>
                    </div>
                    <div className="flex items-center justify-between py-2.5">
                        <dt className="text-sm text-muted-foreground">ID</dt>
                        <dd className="font-mono text-xs text-muted-foreground">{selectedOrganization.organizationCluster.id}</dd>
                    </div>
                </dl>
            </CardContent>
            {roleInstance.isSuperAdmin && (
                <CardFooter className="mt-6 flex justify-end p-0">
                    <Button
                        variant="destructive"
                        onClick={() => setIsDialogOpen(true)}
                    >
                        Terminate Cluster
                    </Button>
                </CardFooter>
            )}

            <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete your cluster
                            &quot;{selectedOrganization.organizationCluster.name}&quot; and remove all associated data.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="my-4 flex flex-col">
                        <Input name='cluster-name' value={inputClusterCheckName} onChange={(e) => setInputClusterCheckName(e.target.value)} />
                        <span className="text-sm text-muted-foreground mt-2"> Write the cluster name  &quot;{selectedOrganization.organizationCluster.name}&quot; to confirm </span>
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <Button variant="destructive" onClick={handleTerminate} disabled={isTerminating || inputClusterCheckName !== selectedOrganization.organizationCluster.name}>
                            {isTerminating ? 'Terminating...' : 'Yes, terminate cluster'}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
}

function StatusIndicator({
    stateName, paused
}) {
    let Icon = Cloud;
    let color = 'text-blue-500';

    if (paused) {
        Icon = Pause;
        color = 'text-gray-500';
    } else if (stateName === 'IDLE') {
        Icon = CheckCircle2;
        color = 'text-green-500';
    } else if (stateName !== 'IDLE' && stateName !== 'CREATING') {
        Icon = AlertCircle;
        color = 'text-yellow-500';
    }

    return <Icon className={`h-5 w-5 ${color}`} />;
}